import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { BellRing, CheckCircle2, Boxes, Sparkles } from "lucide-react";

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");

// Operator-only inbox: jobs on their lines whose production stages were updated to the latest
// BOM version and still need this operator's acknowledgement before logging the next quantity.
export default function OperatorUpdates({ orders = [], currentUsername, onAcknowledge, onNavigate }) {
  const rows = useMemo(
    () => orders.filter((o) => o.ack_required).map((o) => ({
      ...o, acked: (o.acknowledged_by || []).some((a) => a.by === currentUsername),
      newStages: (o.stages || []).filter((s) => s.is_new && (!s.new_until || new Date(s.new_until).getTime() > Date.now())),
    })),
    [orders, currentUsername],
  );
  const pending = rows.filter((r) => !r.acked);
  const done = rows.filter((r) => r.acked);

  const Card = ({ o }) => (
    <div className={`rounded-lg border p-4 ${o.acked ? "border-emerald-200 bg-emerald-50/40" : "border-blue-300 bg-blue-50/50"}`} data-testid={`operator-update-${o.order_code}`}>
      <div className="flex items-start gap-3">
        {o.acked ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" /> : <BellRing className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />}
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800">{o.order_code} · {o.product_name}</p>
          <p className={`text-[11px] mt-0.5 ${o.acked ? "text-emerald-700" : "text-blue-700"}`}>
            {o.acked ? "You acknowledged the updated production stages." : "Production stages updated to the latest BOM version — review the new steps before logging your next quantity."}
          </p>
          {o.newStages.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`operator-update-newstages-${o.order_code}`}>
              {o.newStages.map((s, i) => (
                <span key={`${s.name}-${i}`} className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-600 text-white">
                  <Sparkles className="h-2.5 w-2.5" /> {s.name}
                </span>
              ))}
            </div>
          )}
          <p className="text-[10px] text-slate-400 mt-1">Updated {fmtWhen(o.stages_updated_at)}{o.bom_refresh_reset ? " · some stage progress was reset" : ""}</p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          {!o.acked && (
            <Button size="sm" className="h-7 text-[11px] bg-blue-600 hover:bg-blue-700" onClick={() => onAcknowledge(o)} data-testid={`operator-ack-${o.order_code}`}>
              <CheckCircle2 className="h-3 w-3 mr-1" /> Acknowledge Change
            </Button>
          )}
          {onNavigate && (
            <button onClick={() => onNavigate("schedule")} className="text-[11px] text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-0.5" data-testid={`operator-open-${o.order_code}`}>
              <Boxes className="h-3 w-3" /> View job
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="operator-updates">
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4 flex items-center gap-2">
        <BellRing className="h-5 w-5 text-blue-600" />
        <div>
          <h3 className="font-display text-base font-semibold text-slate-900">Production Updates</h3>
          <p className="text-xs text-slate-400">BOM revisions on your jobs. New or changed stages are highlighted in blue — acknowledge before logging your next quantity.</p>
        </div>
        <span className="ml-auto text-xs text-slate-400" data-testid="operator-updates-counts">{pending.length} to acknowledge · {done.length} acknowledged</span>
      </div>

      <div className="bg-white rounded-xl border border-blue-200 shadow-sm p-4 space-y-3" data-testid="operator-updates-pending">
        <p className="text-sm font-semibold text-blue-700">Needs Your Acknowledgement ({pending.length})</p>
        {pending.length === 0
          ? <p className="text-center text-xs text-slate-400 italic py-4" data-testid="operator-updates-empty">No production changes waiting on you — you're up to date.</p>
          : pending.map((o) => <Card key={o.id} o={o} />)}
      </div>

      {done.length > 0 && (
        <div className="bg-white rounded-xl border border-emerald-200 shadow-sm p-4 space-y-3" data-testid="operator-updates-done">
          <p className="text-sm font-semibold text-emerald-700">Acknowledged ({done.length})</p>
          {done.map((o) => <Card key={o.id} o={o} />)}
        </div>
      )}
    </div>
  );
}
